var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { CommandSocketInitializer } from './framework/CommandSocketInitializer';
import { Timeouts } from './framework/Timeouts';
import { TransportError } from './framework/Transport';
export class PlanarVideoController {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        // other class properties and constructor
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.getDetected(tags);
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`SYSTEM.VERSION?`, /SYSTEM\.VERSION:"?([^\"]+)"?/i, tags);
                return match[1];
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`SYSTEM.POWER?`, /SYSTEM\.POWER:(ON|OFF)/i, tags);
                return match[1].toUpperCase() === 'ON';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`SYSTEM.POWER=${power ? '1' : '0'}`, /SYSTEM\.POWER:(ON|OFF)/i, tags);
                return match[1].toUpperCase() === 'ON';
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`PRESET.ACTIVE?`, /PRESET\.ACTIVE:(\d+)/i, tags);
                return `a${match[1]}`;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const presetIndex = value.slice(1);
                const match = yield this.socket.send(`PRESET.RECALL(${presetIndex})`, /PRESET\.RECALL\(?(\d+)\)?:@ACK/i, tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                return `a${match[1]}`;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            { intervalMs: 5000, controls: ['power', 'videoMatrixRoutes'] },
            {
                // TODO: shorten interval after testing with real device (ideally 10000 ms)
                intervalMs: 30000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    const detected = yield this.getDetected(['poll', 'get.detected']);
                    this.state.detected.update(detected);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.state.busy.update(false);
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            return CommandSocketInitializer.initialize(this.socket, [
                {
                    errorCode: 'verifyFailed',
                    send: `SYSTEM.VERSION?`,
                    next: /SYSTEM\.VERSION:"?(.+)"?/i,
                },
            ], credentials);
        });
    }
    // other class methods
    getDetected(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const videoInputs = (yield this.queryPresetList(tags)).map(preset => ({
                code: `a${preset.index}`,
                external: false,
                name: preset.name,
            }));
            const videoOutputs = [{ code: 'a1', name: 'Output' }];
            return { videoInputs, videoOutputs };
        });
    }
    queryPresetList(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const presetList = [];
            const countMatch = yield this.socket.send('PRESET.COUNT?', /PRESET\.COUNT:(\d+)/i, tags);
            const numPresets = parseInt(countMatch[1], 10);
            // Get preset information for up to 64 presets at a time.
            let pageIndex = 0;
            let numPresetsFound = 0;
            while (numPresetsFound < numPresets && pageIndex < 17) {
                const presetListStartIndex = 1 + pageIndex * 64;
                const presetListEndIndex = Math.min((pageIndex + 1) * 64, 999);
                try {
                    const match = yield this.socket.send(`PRESET.LIST(${presetListStartIndex} ${presetListEndIndex})?`, /PRESET\.LIST(?:\([^\)]+\))?:(.*)|PRESET\.LIST\(\d+\s+\d+\):ERR/i, tags);
                    if (match[1]) {
                        const presetIndices = match[1].split(' ');
                        numPresetsFound += presetIndices.length;
                        for (const presetIndex of presetIndices) {
                            const nameMatch = yield this.socket.send(`PRESET.NAME(${presetIndex})?`, /PRESET\.NAME\(\d+\):"(.*)"/i, tags);
                            const presetName = nameMatch[1] !== '' ? nameMatch[1] : `Preset ${presetIndex}`;
                            presetList.push({ index: presetIndex, name: presetName });
                        }
                    }
                }
                catch (error) {
                    // TODO: We're not sure if the device responds with an error if there are no presets
                    // on the page or if it times out. For now, we look for a timeout, but if the device
                    // responds with an error, we should catch the error instead and remove the
                    // try/catch blocks.
                    if (!(error instanceof TransportError) || error.code !== 'sendTimeout')
                        throw error;
                }
                pageIndex++;
            }
            return presetList;
        });
    }
}
// static properties
PlanarVideoController.driverType = 'socketDriver';
PlanarVideoController.key = '-NI2x9KEXIfEWOuD_Nnf';
PlanarVideoController.baseSocketOptions = {
    commandExtractor: {
        terminators: ['\r'],
    },
    sendTerminator: '\r',
};
PlanarVideoController.socketOptions = [
    Object.assign(Object.assign({}, PlanarVideoController.baseSocketOptions), { port: 57 }),
    Object.assign(Object.assign({}, PlanarVideoController.baseSocketOptions), { port: 4999 }),
];
